import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import {
  Box,
  Typography,
  Paper,
  Divider,
  Stack,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useSelector } from "react-redux";
import { RootState } from "../../store/store";
import { useGetPaymentDetail } from "../../hooks/api/usePayment";
import { useGetReceiptByPayment } from "../../hooks/api/useReceipt";
import {
  BRAND_COLOR,
  BRAND_HOVER,
  PORTAL_PREFIX,
  PAGE_PORTAL_ORDERS_INVOICE_DETAIL,
  PAGE_PORTAL_FINANCE_RECEIPT_DETAIL,
  KivuI18nContext,
  localizedPath,
  useKivunovaTranslation
} from "@kivunova/kivufrontendcommon";
import PrintableContent from "../common/PrintableContent/PrintableContent";
import CommonLoader from "../common/CommonLoader/CommonLoader";
import NoPaymentDetailFound from "./NoPaymentDetailFound";
import { formatDate, formatDateTime } from "../Orders/utils/orderUtils";
import { PaymentMethodLabels } from "./enums/paymentMethod";
import { Payment } from "./types/payment";
import { useReportError } from "../../hooks/useReportError";
import { useFormatCurrency } from "../../hooks/useFormatCurrency";
import { Receipt } from "../Receipt/types/receipt";

export type PaymentDetailPresentation = "modal" | "page";

export interface PaymentDetailViewProps {
  /** When presentation is "modal", may be null when closed */
  paymentId: string | null;
  presentation: PaymentDetailPresentation;
  /** Modal only */
  open?: boolean;
  onClose?: () => void;
}

const PaymentDetailView: React.FC<PaymentDetailViewProps> = ({
  paymentId,
  presentation,
  open = true,
  onClose
}) => {
  const { t } = useKivunovaTranslation();
  const navigate = useNavigate();
  const { language, defaultLang } = useContext(KivuI18nContext) || {};
  const reportError = useReportError();
  const formatCurrency = useFormatCurrency();

  const isModal = presentation === "modal";
  const shouldLoad =
    !!paymentId && (presentation === "page" || (isModal && open));

  const { paymentDetail, paymentList } = useSelector(
    (state: RootState) => state.payments
  );
  const { receiptList } = useSelector((state: RootState) => state.receipts);
  const { mutateAsync: fetchPaymentDetail, isPending } = useGetPaymentDetail();
  const { mutateAsync: fetchReceiptByPayment } = useGetReceiptByPayment();
  const printContentRef = useRef<HTMLDivElement>(null);
  const [receiptForPayment, setReceiptForPayment] = useState<Receipt | null>(
    null
  );

  useEffect(() => {
    if (!shouldLoad || !paymentId) return;
    (async () => {
      try {
        await fetchPaymentDetail({ paymentId });
      } catch (err) {
        reportError("Failed to fetch payment detail (UI)", err, {
          component: "PaymentDetailView",
          paymentId: paymentId ?? ""
        });
      }
    })();
  }, [shouldLoad, paymentId, fetchPaymentDetail, reportError]);

  const payment = useMemo(() => {
    if (!paymentId) return undefined;
    if (
      paymentDetail &&
      (paymentDetail as Payment & { id: string }).id === paymentId
    ) {
      return paymentDetail;
    }
    return paymentList.find(
      (p) => (p as Payment & { id: string }).id === paymentId
    );
  }, [paymentDetail, paymentId, paymentList]);

  const paymentMatches =
    !!payment &&
    paymentId !== null &&
    (payment as Payment & { id: string }).id === paymentId;

  const receiptFromList = useMemo(() => {
    if (!payment) return null;
    const paymentIdValue =
      (payment as Payment & { id: string }).id || payment.paymentNumber;
    return receiptList.find((r) => r.paymentId === paymentIdValue) ?? null;
  }, [payment, receiptList]);

  const linkedReceipt = receiptForPayment ?? receiptFromList;

  const invoiceDetailPath = useMemo(() => {
    if (!paymentMatches || !payment) return "";
    return localizedPath(
      `/${PORTAL_PREFIX}/${PAGE_PORTAL_ORDERS_INVOICE_DETAIL.replace(
        ":invoiceId",
        payment.invoiceId
      )}`,
      language,
      defaultLang
    );
  }, [payment, paymentMatches, language, defaultLang]);

  const receiptDetailPath = useMemo(() => {
    if (!linkedReceipt) return "";
    return localizedPath(
      `/${PORTAL_PREFIX}/${PAGE_PORTAL_FINANCE_RECEIPT_DETAIL.replace(
        ":receiptId",
        linkedReceipt.receiptId
      )}`,
      language,
      defaultLang
    );
  }, [linkedReceipt, language, defaultLang]);

  useEffect(() => {
    if (!shouldLoad || !paymentId) {
      setReceiptForPayment(null);
      return;
    }
    if (isPending) {
      setReceiptForPayment(null);
      return;
    }
    if (!paymentMatches || !payment) return;

    const pid = (payment as Payment & { id: string }).id;
    let cancelled = false;
    setReceiptForPayment(null);

    (async () => {
      try {
        const r = await fetchReceiptByPayment({ paymentId: pid });
        if (!cancelled) setReceiptForPayment(r);
      } catch {
        if (!cancelled) setReceiptForPayment(null);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    shouldLoad,
    paymentId,
    isModal,
    open,
    paymentMatches,
    payment,
    isPending,
    fetchReceiptByPayment
  ]);

  const printableId =
    presentation === "page"
      ? "payment-printable-content-page"
      : "payment-printable-content-modal";

  const detailBody =
    payment && paymentMatches ? (
      <PrintableContent
        id={printableId}
        printTitle={`${t("payment", "Payment")}: ${payment.paymentNumber}`}
        printSubtitle={payment.paymentNumber}
        ref={printContentRef}
      >
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h4" fontWeight="bold" mb={2}>
            {t("payment-detail", "Payment Detail")}
          </Typography>

          <Divider sx={{ my: 2 }} />

          <Stack spacing={2}>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography variant="body2" color="text.secondary">
                {t("invoice", "Invoice")}:
              </Typography>
              {invoiceDetailPath ? (
                <Typography
                  component={RouterLink}
                  to={invoiceDetailPath}
                  variant="body2"
                  fontWeight="medium"
                  sx={{
                    color: BRAND_COLOR,
                    textDecoration: "underline",
                    textUnderlineOffset: 2,
                    textAlign: "right",
                    "&:hover": { color: BRAND_HOVER }
                  }}
                >
                  {t("view-invoice", "View Invoice")}
                </Typography>
              ) : null}
            </Box>

            {linkedReceipt && receiptDetailPath && (
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography variant="body2" color="text.secondary">
                  {t("receipt", "Receipt")}:
                </Typography>
                <Typography
                  component={RouterLink}
                  to={receiptDetailPath}
                  variant="body2"
                  fontWeight="medium"
                  sx={{
                    color: BRAND_COLOR,
                    textDecoration: "underline",
                    textUnderlineOffset: 2,
                    textAlign: "right",
                    "&:hover": { color: BRAND_HOVER }
                  }}
                >
                  {t("view-receipt", "View Receipt")}
                </Typography>
              </Box>
            )}

            <Box display="flex" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">
                {t("payment-method", "Payment Method")}:
              </Typography>
              <Typography variant="body2" fontWeight="medium">
                {PaymentMethodLabels[payment.paymentMethod] ||
                  payment.paymentMethod}
              </Typography>
            </Box>

            <Box display="flex" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">
                {t("payment-date", "Payment Date")}:
              </Typography>
              <Typography variant="body2" fontWeight="medium">
                {formatDate(payment.paymentDate)}
              </Typography>
            </Box>

            {payment.transactionReference && (
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">
                  {t("transaction-reference", "Transaction Reference")}:
                </Typography>
                <Typography variant="body2" fontWeight="medium">
                  {payment.transactionReference}
                </Typography>
              </Box>
            )}

            <Box display="flex" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">
                {t("created", "Created")}:
              </Typography>
              <Typography variant="body2" fontWeight="medium">
                {formatDateTime(
                  (payment as Payment & { createdAt: string }).createdAt
                )}
              </Typography>
            </Box>

            {(payment as Payment & { updatedAt?: string }).updatedAt && (
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">
                  {t("updated", "Updated")}:
                </Typography>
                <Typography variant="body2" fontWeight="medium">
                  {formatDateTime(
                    (payment as Payment & { updatedAt: string }).updatedAt
                  )}
                </Typography>
              </Box>
            )}

            <Divider />

            <Box display="flex" justifyContent="space-between">
              <Typography variant="body2" fontWeight="bold">
                {t("amount", "Amount")}:
              </Typography>
              <Typography variant="body2" fontWeight="bold">
                {formatCurrency(payment.amount)}
              </Typography>
            </Box>

            {payment.notes && (
              <>
                <Divider />
                <Box>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    {t("notes", "Notes")}:
                  </Typography>
                  <Typography variant="body2">{payment.notes}</Typography>
                </Box>
              </>
            )}
          </Stack>
        </Paper>
      </PrintableContent>
    ) : null;

  if (presentation === "page") {
    if (!paymentId) {
      return <NoPaymentDetailFound />;
    }

    if (isPending) {
      return (
        <CommonLoader
          loadingMessage={t(
            "fetching-payment-details",
            "Fetching payment details..."
          )}
        />
      );
    }

    if (!paymentMatches) {
      return <NoPaymentDetailFound />;
    }

    return (
      <Box sx={{ p: { xs: 2, sm: 3 } }}>
        <Box
          sx={{
            mb: 2.5,
            width: "100%",
            display: "flex",
            flexDirection: "column",
            gap: { xs: 1, sm: 1.25 }
          }}
        >
          <Box
            component="button"
            type="button"
            onClick={() => navigate(-1)}
            aria-label={t("back-to-payments", "Back to Payments")}
            sx={{
              display: { xs: "inline-flex", md: "none" },
              alignSelf: "flex-start",
              alignItems: "center",
              gap: 0.35,
              py: 0,
              mr: "auto",
              border: "none",
              background: "none",
              cursor: "pointer",
              font: "inherit",
              color: BRAND_COLOR,
              fontWeight: 500,
              fontSize: "0.8125rem",
              lineHeight: 1.2,
              textDecoration: "none",
              opacity: 0.92,
              transition: "opacity 0.15s ease, color 0.15s ease",
              "&:hover": {
                color: BRAND_HOVER,
                opacity: 1
              }
            }}
          >
            <ArrowBackIcon sx={{ fontSize: 16, flexShrink: 0 }} />
            {t("back", "Back")}
          </Box>
        </Box>
        {detailBody}
      </Box>
    );
  }

  /* modal */
  return (
    <Dialog
      open={Boolean(open && paymentId)}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      scroll="paper"
      aria-labelledby="payment-detail-modal-title"
    >
      <DialogTitle
        id="payment-detail-modal-title"
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          py: 1,
          pr: 1
        }}
      >
        <IconButton
          aria-label={t("close", "Close")}
          onClick={onClose}
          size="small"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ pt: 0 }}>
        {open && paymentId && isPending && (
          <CommonLoader
            loadingMessage={t(
              "fetching-payment-details",
              "Fetching payment details..."
            )}
          />
        )}

        {open && paymentId && !isPending && !paymentMatches && (
          <Typography color="text.secondary">
            {t("payment-not-found", "Payment could not be loaded.")}
          </Typography>
        )}

        {open &&
          paymentId &&
          !isPending &&
          paymentMatches &&
          payment &&
          detailBody}
      </DialogContent>
    </Dialog>
  );
};

export default PaymentDetailView;
